import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Copy, Search, ArrowRight, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { fmtMDY } from "@/lib/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "wi-pa-wizard-v1";

const STATUS_PILL = {
  approved:    { label: "Approved",    tone: "bg-[#d9e6df] text-[#0A3D79] border-[#0A3D79]",  icon: CheckCircle2 },
  submitted:   { label: "Submitted",   tone: "bg-[#E7F1FB] text-[#0A3D79] border-[#1FA0E6]",  icon: Clock },
  signed_back: { label: "Signed back", tone: "bg-[#E7F1FB] text-[#0A3D79] border-[#1FA0E6]",  icon: Clock },
  generated:   { label: "Generated",   tone: "bg-[#E7F1FB] text-[#3A5275] border-[#C1D1E1]",  icon: Clock },
  denied:      { label: "Denied",      tone: "bg-[#fbeae5] text-[#c05746] border-[#c05746]",  icon: AlertTriangle },
};

/**
 * Modal that lists previous submissions and lets a nurse seed a new draft
 * from any of them. The backend scrubs visit-specific fields (signatures,
 * assessment date, cert period, vitals) before returning, so the nurse
 * only needs to confirm what's changed plus capture today's observations.
 */
export default function CopyFromLastModal({ open, onClose, onCopied }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [copying, setCopying] = useState(null); // job_id being copied

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/submissions`)
      .then((res) => setRows(res.data.submissions || []))
      .catch((e) => toast.error(`Failed to load: ${e.message}`))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const hay = [r.client_name, r.agency_name, r.client_member_id].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [rows, search]);

  const copyFrom = async (job) => {
    setCopying(job.job_id);
    try {
      const res = await axios.get(`${API}/submissions/${job.job_id}/snapshot`);
      const payload = res.data?.payload || {};
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        // Keep a reference to the source submission so the verify screen
        // can diff "what changed vs. last visit" — helpful on recerts.
        localStorage.setItem(
          "wi-pa-copy-source-v1",
          JSON.stringify({
            job_id: job.job_id,
            client_name: job.client_name,
            copied_at: Date.now(),
            snapshot: payload,  // the scrubbed baseline we seeded from
          })
        );
      } catch {
        toast.error("Your browser blocked local storage — can't seed the draft.");
        setCopying(null);
        return;
      }
      toast.success(`Seeded from ${job.client_name || "previous visit"}. Review each step — visit date, vitals, and signatures were cleared.`, { duration: 6000 });
      onCopied?.();
      onClose?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 404 && detail?.includes("not available")) {
        toast.error("This submission predates the snapshot feature — start a fresh intake.");
      } else {
        toast.error(`Copy failed: ${detail || e.message}`);
      }
    } finally {
      setCopying(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[150] bg-[#0B1E3A]/55 backdrop-blur-sm flex items-center justify-center px-4 py-8"
      data-testid="copy-from-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[82vh] flex flex-col shadow-2xl border border-[#D6E3EF]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-7 pb-5 border-b border-[#E7EEF3]">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-[#FFDA0B] p-2 mt-0.5">
              <Copy className="w-5 h-5 text-[#072A54]" strokeWidth={2} />
            </div>
            <div>
              <div className="eyebrow mb-1">Recert / reassessment</div>
              <h3 className="font-display text-[20px] font-semibold text-[#0B1E3A] tracking-tight">
                Copy from last <span className="yellow-underline">submission</span>
              </h3>
              <p className="text-[12.5px] text-[#3A5275] mt-1.5">
                Reuse demographics, diagnoses, medications, and care plan. Visit date, vitals, and signatures will be cleared so you capture them fresh.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#3A5275] hover:text-[#0B1E3A] p-1" data-testid="copy-from-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-[#D6E3EF]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#3A5275]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by client, Member ID, or agency…"
              className="tb-input pl-9"
              data-testid="copy-from-search"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center gap-2 text-[#3A5275] p-6" data-testid="copy-from-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading submissions…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10" data-testid="copy-from-empty">
              <div className="font-display text-lg text-[#0B1E3A] mb-1">
                {rows.length === 0 ? "No previous submissions yet" : "No submissions match your search"}
              </div>
              <p className="text-[13px] text-[#3A5275]">
                {rows.length === 0
                  ? "Complete an intake and it'll appear here for your next visit."
                  : "Try a different search term."}
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {filtered.map((r) => {
                const s = STATUS_PILL[r.status] || STATUS_PILL.generated;
                const StatusIcon = s.icon;
                return (
                  <li key={r.job_id}>
                    <button
                      type="button"
                      onClick={() => copyFrom(r)}
                      disabled={!!copying}
                      className="w-full text-left rounded-xl border border-[#D6E3EF] hover:border-[#0A3D79] hover:bg-[#F0F6FC] px-4 py-3 flex items-center gap-3 transition disabled:opacity-60"
                      data-testid={`copy-from-${r.job_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#0B1E3A] text-[14.5px]">
                            {r.client_name || "Unnamed client"}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 h-5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider border ${s.tone}`}>
                            <StatusIcon className="w-2.5 h-2.5" />{s.label}
                          </span>
                        </div>
                        <div className="text-[12.5px] text-[#3A5275] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                          {r.client_member_id && <span>Member {r.client_member_id}</span>}
                          {r.agency_name && <span>{r.agency_name}</span>}
                          <span>{fmtMDY(r.generated_at) || "—"}</span>
                        </div>
                      </div>
                      {copying === r.job_id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[#0A3D79]" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#0A3D79]" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#D6E3EF] bg-[#F7FAFC] flex items-center justify-between text-[12px] text-[#3A5275]">
          <span className="inline-flex items-center gap-1">
            <ArrowRight className="w-3.5 h-3.5" />
            Your current draft will be replaced.
          </span>
          <button onClick={onClose} className="btn-ghost !h-8 !px-3 text-[12px]" data-testid="copy-from-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
